import { Button, Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from '@repo/ui'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

function getPageNumbers(currentPage: number, pageCount: number): Array<number | 'ellipsis'> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }

  const pages: Array<number | 'ellipsis'> = [1]

  if (currentPage > 3) {
    pages.push('ellipsis')
  }

  const start = Math.max(2, currentPage - 1)
  const end = Math.min(pageCount - 1, currentPage + 1)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  if (currentPage < pageCount - 2) {
    pages.push('ellipsis')
  }

  pages.push(pageCount)
  return pages
}

export function ContentPagination({
  currentPage,
  pageCount,
  onPageChange
}: {
  currentPage: number
  pageCount: number
  onPageChange: (page: number) => void
}) {
  const pages = getPageNumbers(currentPage, pageCount)

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <Button
            variant="ghost"
            size="default"
            className="gap-1 px-2.5 sm:pl-2.5"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Go to previous page"
          >
            <ChevronLeftIcon className="size-4" />
            <span className="hidden sm:block">Previous</span>
          </Button>
        </PaginationItem>

        {pages.map((page, i) =>
          page === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <Button
                variant={page === currentPage ? 'outline' : 'ghost'}
                size="icon"
                onClick={() => onPageChange(page)}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </Button>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <Button
            variant="ghost"
            size="default"
            className="gap-1 px-2.5 sm:pr-2.5"
            disabled={currentPage >= pageCount}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Go to next page"
          >
            <span className="hidden sm:block">Next</span>
            <ChevronRightIcon className="size-4" />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
